import React, { useState, useEffect } from "react";
import styles from "./ExpenseItemPanel.module.css";

export default function ExpenseItemPanel({ onItemsChange, initialItems }) {
  // ✅ initialItems는 "이전 건 복사" 등 외부에서 최초 진입 값을 주입할 때만 사용 —
  // 부모가 key prop을 바꿔 리마운트시켜야 반영된다(내부 상태는 마운트 시 1회만 초기화됨)
  const [items, setItems] = useState(() => initialItems || []);
  const [form, setForm] = useState({
    name: "",
    unit: "",
    qty: "",
    price: "",
    supply: "",
    vat: "",
    remark: "",
  });

  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState({});

  const comma = (n) =>
    n ? n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
  const uncomma = (n) => n.replace(/,/g, "");

  // 공급가액, 부가세 계산 코드 부가세를 먼저 계산 한 후 반올림 후 공급가액 계산
  const calc = (qty, price) => {
    const total = qty * price;
    const vat = Math.round(total / 11);  // 부가세 10% = 총액의 1/11
    const supply = total - vat;
    return { total, supply, vat };
  };

  // ✅ 수량이나 단가가 변경되면 자동으로 공급가액과 부가세 계산
  useEffect(() => {
    const qty = parseInt(uncomma(form.qty));
    const price = parseInt(uncomma(form.price));
    
    if (!isNaN(qty) && !isNaN(price) && qty > 0 && price > 0) {
      const calculated = calc(qty, price);
      setForm(prev => ({
        ...prev,
        supply: comma(calculated.supply.toString()),
        vat: comma(calculated.vat.toString())
      }));
    }
  }, [form.qty, form.price]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // 숫자 필드는 쉼표 처리
    if (['qty', 'price', 'supply', 'vat'].includes(name)) {
      setForm({
        ...form,
        [name]: comma(value.replace(/,/g, "")),
      });
    } else {
      setForm({
        ...form,
        [name]: value,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.name === "remark") {
      e.preventDefault();
      addItem();
    }
  };

  const addItem = () => {
    const qty = parseInt(uncomma(form.qty));
    const price = parseInt(uncomma(form.price));
    if (!form.name) return alert("품목명을 입력하세요");
    if (isNaN(qty) || isNaN(price)) return alert("수량/단가 숫자 입력");

    let supply = parseInt(uncomma(form.supply));
    let vat = parseInt(uncomma(form.vat));
    
    // 공급가액이나 부가세가 비어있으면 자동 계산
    if (isNaN(supply) || isNaN(vat)) {
      const t = calc(qty, price);
      supply = t.supply;
      vat = t.vat;
    }

    const newItem = {
      name: form.name,
      unit: form.unit,
      remark: form.remark,
      qty,
      price,
      supply,
      vat,
      total: supply + vat,
    };

    setItems([...items, newItem]);
    onItemsChange && onItemsChange([...items, newItem]);

    setForm({
      name: "",
      unit: "",
      qty: "",
      price: "",
      supply: "",
      vat: "",
      remark: "",
    });
  };

  const removeItem = (idx) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    const updated = items.filter((_, i) => i !== idx);
    setItems(updated);
    onItemsChange && onItemsChange(updated);
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    setEditForm(items[idx]);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: comma(value.replace(/,/g, "")),
    });
  };

  const saveEdit = () => {
    const qty = parseInt(uncomma(editForm.qty));
    const price = parseInt(uncomma(editForm.price));
    let supply = parseInt(uncomma(editForm.supply));
    let vat = parseInt(uncomma(editForm.vat));

    if (isNaN(supply) || isNaN(vat)) {
      const t = calc(qty, price);
      supply = t.supply;
      vat = t.vat;
    }

    const updated = [...items];
    updated[editIdx] = {
      ...editForm,
      qty,
      price,
      supply,
      vat,
      total: supply + vat,
    };
    setItems(updated);
    setEditIdx(null);
  };

  const sumSupply = items.reduce((a, b) => a + b.supply, 0);
  const sumVat = items.reduce((a, b) => a + b.vat, 0);
  const sumTotal = items.reduce((a, b) => a + b.total, 0);

  return (
    <div className={styles.sidePane}>
  
      {/* ✅ 입력 + 데이터 통합 테이블 */}
      <div className={styles.scrollWrap}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th>[ALT+6] 품목명</th>
              <th>단위</th>
              <th>수량</th>
              <th>단가</th>
              <th>금액(자동)</th>
              <th>공급가액(자동)</th>
              <th>부가세(자동)</th>
              <th>비고</th>
              <th>추가</th>
            </tr>
          </thead>
          <tbody>
            {/* 입력행 */}
            <tr className={styles.inputRow}>
              <td><input name="name" value={form.name} onChange={handleChange} /></td>
              <td><input name="unit" value={form.unit} onChange={handleChange} /></td>
              <td><input name="qty" value={form.qty} onChange={handleChange} /></td>
              <td><input name="price" value={form.price} onChange={handleChange} /></td>
              <td className={styles.calcCell}>{comma(form.qty && form.price ? uncomma(form.qty) * uncomma(form.price) : 0)}</td>
              <td><input name="supply" value={form.supply} onChange={handleChange} /></td>
              <td><input name="vat" value={form.vat} onChange={handleChange} /></td>
              <td><input name="remark" value={form.remark} onChange={handleChange} onKeyDown={handleKeyDown} /></td>
              <td><button onClick={addItem}>추가</button></td>
            </tr>
            
            {/* 데이터 행들 */}
            {items.map((item, idx) => (
              <tr key={idx}>
                <td>{item.name}</td>
                <td>{item.unit}</td>
                <td>{comma(item.qty)}</td>
                <td>{comma(item.price)}</td>
                <td>{comma(item.total)}</td>
                <td>{comma(item.supply)}</td>
                <td>{comma(item.vat)}</td>
                <td>{item.remark}</td>
                <td><button onClick={() => removeItem(idx)}>삭제</button></td>
              </tr>
            ))}
  
            {/* 합계 행 */}
            <tr className={styles.sumRow}>
              <td>합계</td>
              <td></td><td></td><td></td>
              <td>{comma(sumTotal)}</td>
              <td>{comma(sumSupply)}</td>
              <td>{comma(sumVat)}</td>
              <td></td><td></td>
            </tr>
          </tbody>
        </table>
      </div>
  
    </div>
  );
}